import { useState, useMemo, useEffect, type KeyboardEvent } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ArrowLeft,
  ArrowUpDown,
  Download,
  Filter,
  Upload,
  LayoutGrid,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  Table2,
  X,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SmartImportWizard } from "@/components/smart-import-wizard";
import { PaginationBar } from "@/components/pagination-bar";
import { PreprocessingProgress } from "@/components/preprocessing-progress";
import { itemDisplayName } from "@/lib/inventory";
import { ConfigSelect } from "@/components/config-select";
import { configOptions, COUNTRY_OPTIONS } from "@/lib/config-options";
import { enCatalog, translate, type TFunction } from "@/i18n/catalog";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────────

interface InventoryList {
  id: string;
  name: string;
  type: string;
  list_code: string;
  country_code: string | null;
  is_active: boolean;
  item_count: number;
  created_at: string;
  updated_at: string;
}

interface InventoryItem {
  id: string;
  list_id: string;
  description: string;
  clean_name: string | null;
  simple_name: string | null;
  sku: string | null;
  category_id: string | null;
  subcategory_id: string | null;
  storage_type_id: string | null;
  unit_pack_size: string | null;
  price: number | null;
  currency: string;
  price_per_kg: number | null;
  typical_shelf_life_days: number | null;
  typical_weight_kg: number | null;
  edible_yield_pct: number | null;
  allergens: string[];
  aliases: string[];
  supplier: string | null;
  status: string;
  storage_types: { name: string; display_name: string } | null;
  category: { name: string } | null;
  subcategory: { name: string } | null;
  recipe_count: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface FoodCategory {
  id: string;
  name: string;
  slug: string;
  parent_id: string | null;
  sort_order: number;
}

interface StorageType {
  id: string;
  name: string;
  display_name: string;
  sort_order: number;
}

/** `/admin/*` is English by pinned decision 4, so the shared country list resolves against
 * the English catalogue rather than the viewer's locale — the same one-liner
 * `admin/organisations.tsx` carries, which is the other end of this comparison. */
const englishT: TFunction = (key, params) => translate([enCatalog], "en", key, params);

const COUNTRY_LABELS = configOptions(englishT, COUNTRY_OPTIONS);
const UNSET_COUNTRY_OPTIONS = [{ value: "", label: "Not set" }, ...COUNTRY_LABELS];

// ── Schemas ────────────────────────────────────────────────────────────

const createListSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["master_default", "country_default"]),
  list_code: z
    .string()
    .length(2, "Must be 2 letters")
    .regex(/^[A-Za-z]{2}$/, "Must be 2 letters")
    .transform((v) => v.toUpperCase()),
  country_code: z.string().optional(),
});

type CreateListForm = z.infer<typeof createListSchema>;

// Preprocess empty strings to undefined so optional number fields don't fail
// validation (HTML inputs send "" for empty, z.coerce.number() turns "" into 0,
// and .positive() rejects 0 — silently blocking form submission).
const optNum = z.preprocess(
  (v) => (v === "" || v === null || v === undefined ? undefined : v),
  z.coerce.number().positive().optional(),
);
const optInt = z.preprocess(
  (v) => (v === "" || v === null || v === undefined ? undefined : v),
  z.coerce.number().int().positive().optional(),
);
const optPct = z.preprocess(
  (v) => (v === "" || v === null || v === undefined ? undefined : v),
  z.coerce.number().min(0).max(100).optional(),
);

const itemFormSchema = z.object({
  description: z.string().min(1, "Description is required"),
  sku: z.string().optional(),
  category_id: z.string().optional(),
  subcategory_id: z.string().optional(),
  storage_type_id: z.string().optional(),
  unit_pack_size: z.string().optional(),
  price: optNum,
  currency: z.string().default("EUR"),
  price_per_kg: optNum,
  typical_shelf_life_days: optInt,
  typical_weight_kg: optNum,
  edible_yield_pct: optPct,
  supplier: z.string().optional(),
  status: z
    .enum(["active", "inactive", "pending_review", "discontinued", "seasonal"])
    .default("active"),
});

type ItemFormData = z.infer<typeof itemFormSchema>;

// ── Constants ──────────────────────────────────────────────────────────

const EU_14_ALLERGENS = [
  "celery",
  "crustaceans",
  "eggs",
  "fish",
  "gluten",
  "lupin",
  "milk",
  "molluscs",
  "mustard",
  "nuts",
  "peanuts",
  "sesame",
  "soy",
  "sulphites",
] as const;

// ── Helpers ────────────────────────────────────────────────────────────

function typeLabel(type: string): string {
  return type === "master_default"
    ? "Master Catalogue"
    : type === "country_default"
      ? "Country Catalogue"
      : type;
}

function itemFormToBody(
  data: ItemFormData,
  allergens: string[],
  aliases: string[],
  cleanName?: string,
  simpleName?: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    description: data.description,
    status: data.status,
    currency: data.currency,
    allergens,
    aliases,
  };
  if (cleanName !== undefined) body.clean_name = cleanName || null;
  if (simpleName !== undefined) body.simple_name = simpleName || null;
  if (data.sku) body.sku = data.sku;
  if (data.category_id) body.category_id = data.category_id;
  if (data.subcategory_id) body.subcategory_id = data.subcategory_id;
  if (data.storage_type_id) body.storage_type_id = data.storage_type_id;
  if (data.unit_pack_size) body.unit_pack_size = data.unit_pack_size;
  if (data.price) body.price = Number(data.price);
  if (data.price_per_kg) body.price_per_kg = Number(data.price_per_kg);
  if (data.typical_shelf_life_days)
    body.typical_shelf_life_days = Number(data.typical_shelf_life_days);
  if (data.typical_weight_kg) body.typical_weight_kg = Number(data.typical_weight_kg);
  if (data.edible_yield_pct != null) body.edible_yield_pct = Number(data.edible_yield_pct);
  if (data.supplier) body.supplier = data.supplier;
  return body;
}

/** Try to extract weight in kg from unit string like "5kg box", "2.5L" */
function parseWeightFromUnit(unit: string): number | null {
  const m = unit.match(/^([\d.]+)\s*(kg|g|l|ml)/i);
  if (!m) return null;
  const val = parseFloat(m[1]);
  const u = m[2].toLowerCase();
  if (u === "kg" || u === "l") return val;
  if (u === "g" || u === "ml") return val / 1000;
  return null;
}

// ── Main Component ─────────────────────────────────────────────────────

export default function AdminCataloguesPage() {
  const [selectedList, setSelectedList] = useState<InventoryList | null>(null);
  return selectedList ? (
    <ItemManagementView list={selectedList} onBack={() => setSelectedList(null)} />
  ) : (
    <ListManagementView onSelectList={setSelectedList} />
  );
}

// ── List Management View ───────────────────────────────────────────────

function ListManagementView({ onSelectList }: { onSelectList: (l: InventoryList) => void }) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const listsQuery = useQuery<InventoryList[]>({
    queryKey: ["admin", "inventory-lists"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-lists`, { headers });
      if (!res.ok) throw new Error(`${res.status}`);
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateListForm) => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-lists`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: data.name,
          type: data.type,
          list_code: data.list_code,
          country_code: data.type === "country_default" ? data.country_code : null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail ?? "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "inventory-lists"] });
      toast.success("List created");
      setOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<CreateListForm>({
    resolver: zodResolver(createListSchema),
    defaultValues: { type: "country_default" },
  });
  const listType = watch("type");

  const [editList, setEditList] = useState<InventoryList | null>(null);
  const editMutation = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-lists/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail ?? "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "inventory-lists"] });
      toast.success("Catalogue updated");
      setEditList(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900">Catalogues</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage master and country catalogues for organisations.
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) reset();
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-1.5 size-4" />
              New List
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Inventory List</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit((d) => createMutation.mutate(d))} className="space-y-4">
              <div className="grid grid-cols-[1fr_80px] gap-3">
                <div>
                  <label className="mb-1.5 block text-sm text-gray-500" htmlFor="name">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <Input id="name" {...register("name")} />
                  {errors.name && (
                    <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <label className="mb-1.5 block text-sm text-gray-500" htmlFor="list_code">
                    Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    id="list_code"
                    maxLength={2}
                    className="uppercase"
                    placeholder="ML"
                    {...register("list_code")}
                  />
                  {errors.list_code && (
                    <p className="mt-1 text-xs text-red-600">{errors.list_code.message}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm text-gray-500" htmlFor="list-type">
                  Type
                </label>
                <Select
                  value={listType}
                  onValueChange={(v) => setValue("type", v as CreateListForm["type"])}
                >
                  <SelectTrigger id="list-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="master_default">Master Catalogue</SelectItem>
                    <SelectItem value="country_default">Country Catalogue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {listType === "country_default" && (
                <div>
                  <label className="mb-1.5 block text-sm text-gray-500" htmlFor="country_code">
                    Country Code (ISO 3166-1)
                  </label>
                  {/* The same list the organisation forms offer, which is the point: this
                      column and `organisations.country_code` are compared exactly, so two
                      free-text fields could drift into a pair that resolves to nothing
                      (POS2-1144). "Not set" stays selectable so the endpoint's own
                      "country_code required for country_default" still answers, rather than a
                      422. */}
                  <ConfigSelect
                    id="country_code"
                    options={UNSET_COUNTRY_OPTIONS}
                    {...register("country_code")}
                  />
                </div>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={!isValid || createMutation.isPending}
              >
                {createMutation.isPending ? "Creating..." : "Create List"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {listsQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}
      {listsQuery.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">Failed to load lists</p>
        </div>
      )}
      {listsQuery.data?.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
          <Package className="size-8" />
          <p className="text-sm">No default inventory lists</p>
        </div>
      )}

      {listsQuery.data && listsQuery.data.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Country</TableHead>
              <TableHead className="text-right">Items</TableHead>
              <TableHead className="text-right">Updated</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {listsQuery.data.map((list) => (
              <TableRow key={list.id} className="cursor-pointer" onClick={() => onSelectList(list)}>
                <TableCell className="font-medium text-gray-900">{list.name}</TableCell>
                <TableCell className="font-mono text-xs text-gray-500">{list.list_code}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{typeLabel(list.type)}</Badge>
                </TableCell>
                <TableCell className="text-xs text-gray-400">{list.country_code ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{list.item_count}</TableCell>
                <TableCell className="text-right text-xs text-gray-400">
                  {new Date(list.updated_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <button
                    type="button"
                    aria-label="Edit catalogue"
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditList(list);
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editList && (
        <EditListDialog
          list={editList}
          saving={editMutation.isPending}
          onClose={() => setEditList(null)}
          onSave={(body) => editMutation.mutate({ id: editList.id, body })}
        />
      )}
    </div>
  );
}

function EditListDialog({
  list,
  saving,
  onClose,
  onSave,
}: {
  list: InventoryList;
  saving: boolean;
  onClose: () => void;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const isCountry = list.type === "country_default";
  const [name, setName] = useState(list.name);
  const [countryCode, setCountryCode] = useState(list.country_code ?? "");

  function submit() {
    const body: Record<string, unknown> = { name: name.trim() };
    if (isCountry && countryCode.trim()) body.country_code = countryCode.trim();
    onSave(body);
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit {typeLabel(list.type)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm text-gray-500" htmlFor="edit-cat-name">
              Name
            </label>
            <Input id="edit-cat-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {isCountry ? (
            <div>
              <label className="mb-1.5 block text-sm text-gray-500" htmlFor="edit-cat-country">
                Country Code (ISO 3166-1)
              </label>
              {/* No empty option here, unlike the create dialog: a country catalogue always
                  has a country, and blanking it would be dropped by the submit below. */}
              <ConfigSelect
                id="edit-cat-country"
                options={COUNTRY_LABELS}
                current={list.country_code}
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              />
            </div>
          ) : (
            <p className="text-xs text-gray-400">
              The master catalogue applies to everyone and has no country code.
            </p>
          )}
          <Button className="w-full" disabled={!name.trim() || saving} onClick={submit}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Item Management View ───────────────────────────────────────────────

function ItemManagementView({ list, onBack }: { list: InventoryList; onBack: () => void }) {
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [view, setView] = useState<"table" | "cards">("table");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [importOpen, setImportOpen] = useState(false);

  // Auto-detect active processing on this list
  const processingQuery = useQuery<{ status: string; total: number }>({
    queryKey: ["preprocessing-status", list.id],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${list.id}/preprocess/status`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()).data;
    },
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" ? 3000 : false;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });
  const listProcessing = processingQuery.data?.status === "processing";

  async function handlePreprocess() {
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${list.id}/preprocess`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 409) {
          toast.info("Processing already in progress");
          return;
        }
        throw new Error(body.detail ?? "Failed to start processing");
      }
      processingQuery.refetch();
      setProcessingDialogOpen(true);
    } catch {
      toast.error("Failed to start processing");
    }
  }

  async function handleReprocess() {
    const count = processingQuery.data?.total ?? list.item_count ?? 0;
    if (
      !confirm(
        `This will regenerate search names for all ${count.toLocaleString()} items. This usually takes a few minutes.\n\nContinue?`,
      )
    )
      return;

    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${list.id}/reprocess`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (res.status === 409) {
          toast.info("Processing already in progress");
          return;
        }
        throw new Error(body.detail ?? "Failed to start reprocessing");
      }
      processingQuery.refetch();
      setProcessingDialogOpen(true);
    } catch {
      toast.error("Failed to start reprocessing");
    }
  }

  async function handleExport() {
    try {
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-lists/${list.id}/export`, {
        headers: h,
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${list.list_code}-inventory-export.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export");
    }
  }
  const [filterStorageTypes, setFilterStorageTypes] = useState<string[]>([]);
  const [filterAllergens, setFilterAllergens] = useState<string[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterSubcategoryId, setFilterSubcategoryId] = useState("");
  const [shelfLifeRange, setShelfLifeRange] = useState<[number, number]>([0, 30]);
  const shelfLifeActive = shelfLifeRange[0] > 0 || shelfLifeRange[1] < 30;

  const activeFilterCount = [
    filterStorageTypes.length > 0,
    filterAllergens.length > 0,
    !!filterCategoryId,
    shelfLifeActive,
  ].filter(Boolean).length;

  function clearFilters() {
    setFilterStorageTypes([]);
    setFilterAllergens([]);
    setFilterCategoryId("");
    setFilterSubcategoryId("");
    setShelfLifeRange([0, 30]);
    setPage(0);
  }

  function toggleSort(field: string) {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(0);
  }

  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const itemsQuery = useQuery<InventoryItem[]>({
    queryKey: [
      "admin",
      "inventory-items",
      list.id,
      search,
      page,
      sortBy,
      sortDir,
      pageSize,
      filterStorageTypes,
      filterAllergens,
      filterCategoryId,
      filterSubcategoryId,
      shelfLifeRange,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (search) params.set("search", search);
      for (const id of filterStorageTypes) params.append("storage_type_id", id);
      for (const a of filterAllergens) params.append("allergen", a);
      if (filterCategoryId) params.append("category_id", filterCategoryId);
      if (filterSubcategoryId) params.append("subcategory_id", filterSubcategoryId);
      if (shelfLifeRange[0] > 0) params.set("shelf_life_min", String(shelfLifeRange[0]));
      if (shelfLifeRange[1] < 30) params.set("shelf_life_max", String(shelfLifeRange[1]));
      const res = await fetch(
        `${API_URL}/api/v1/admin/inventory-lists/${list.id}/items?${params}`,
        { headers },
      );
      if (!res.ok) throw new Error("Failed to load items");
      const count = res.headers.get("X-Total-Count");
      if (count) setTotalCount(Number(count));
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    refetchInterval: listProcessing ? 5000 : false,
  });

  const categoriesQuery = useQuery<FoodCategory[]>({
    queryKey: ["admin", "food-categories"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/food-categories`, { headers });
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const storageTypesQuery = useQuery<StorageType[]>({
    queryKey: ["admin", "storage-types"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/admin/storage-types`, { headers });
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const topCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((c) => !c.parent_id),
    [categoriesQuery.data],
  );
  function subcategoriesFor(parentId: string) {
    return (categoriesQuery.data ?? []).filter((c) => c.parent_id === parentId);
  }

  function invalidateItems() {
    queryClient.invalidateQueries({ queryKey: ["admin", "inventory-items", list.id] });
    queryClient.invalidateQueries({ queryKey: ["admin", "inventory-lists"] });
  }

  const createItemMutation = useMutation({
    mutationFn: async ({
      data,
      allergens,
      aliases,
    }: {
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
    }) => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-lists/${list.id}/items`, {
        method: "POST",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases)),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(err.detail ?? "Failed");
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateItems();
      toast.success("Item added");
      setCreateOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-items/${itemId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => {
      invalidateItems();
      toast.success("Item deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      data,
      allergens,
      aliases,
      cleanName,
      simpleName,
    }: {
      itemId: string;
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
      cleanName?: string;
      simpleName?: string;
    }) => {
      const res = await fetch(`${API_URL}/api/v1/admin/inventory-items/${itemId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases, cleanName, simpleName)),
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      invalidateItems();
      toast.success("Item updated");
      setEditingItem(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const items = itemsQuery.data ?? [];

  if (importOpen) {
    return <SmartImportWizard listId={list.id} open={importOpen} onOpenChange={setImportOpen} />;
  }

  return (
    <div className="absolute inset-0 flex flex-col gap-4 bg-gray-50/80 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg font-medium text-gray-900">{list.name}</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              <Badge variant="secondary" className="mr-2">
                {typeLabel(list.type)}
              </Badge>
              {list.item_count} items{list.country_code && ` · ${list.country_code}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Popover modal={false}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="relative size-8">
                <Filter className="size-4" />
                {activeFilterCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-gray-900 text-[10px] text-white">
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 space-y-4 p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900">Filters</p>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    className="text-xs text-gray-400 hover:text-gray-600"
                    onClick={clearFilters}
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div>
                <p className="mb-1.5 text-xs text-gray-400">Storage Type</p>
                <PillSelector
                  id="filter-storage"
                  options={(storageTypesQuery.data ?? []).map((st) => ({
                    id: st.id,
                    label: st.display_name,
                  }))}
                  multi
                  selected={filterStorageTypes}
                  onToggle={(v) => {
                    setFilterStorageTypes((prev) =>
                      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                    );
                    setPage(0);
                  }}
                />
              </div>
              <div>
                <p className="mb-1.5 text-xs text-gray-400">Allergens</p>
                <PillSelector
                  id="filter-allergens"
                  options={EU_14_ALLERGENS.map((a) => ({ id: a, label: a }))}
                  multi
                  selected={filterAllergens}
                  onToggle={(v) => {
                    setFilterAllergens((prev) =>
                      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                    );
                    setPage(0);
                  }}
                />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-400">Category</p>
                <SearchSelect
                  options={topCategories.map((c) => ({ id: c.id, label: c.name }))}
                  value={filterCategoryId}
                  onChange={(v) => {
                    setFilterCategoryId(v);
                    setFilterSubcategoryId("");
                    setPage(0);
                  }}
                  placeholder="Search categories..."
                />
              </div>
              <div>
                <p className="mb-1 text-xs text-gray-400">Subcategory</p>
                <SearchSelect
                  options={(filterCategoryId
                    ? subcategoriesFor(filterCategoryId)
                    : (categoriesQuery.data ?? []).filter((c) => c.parent_id)
                  ).map((c) => ({ id: c.id, label: c.name }))}
                  value={filterSubcategoryId}
                  onChange={(v) => {
                    setFilterSubcategoryId(v);
                    setPage(0);
                  }}
                  placeholder="Search subcategories..."
                />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs text-gray-400">Shelf Life</p>
                  <p className="text-xs tabular-nums text-gray-500">
                    {shelfLifeRange[0]}d–
                    {shelfLifeRange[1] >= 30 ? "30d+" : `${shelfLifeRange[1]}d`}
                  </p>
                </div>
                <Slider
                  min={0}
                  max={30}
                  step={1}
                  value={shelfLifeRange}
                  onValueChange={(v) => {
                    setShelfLifeRange(v as [number, number]);
                    setPage(0);
                  }}
                />
              </div>
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="size-8">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setImportOpen(true)}>
                <Upload className="mr-2 size-4" />
                Import CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport()}>
                <Download className="mr-2 size-4" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handlePreprocess()} disabled={listProcessing}>
                <Sparkles className="mr-2 size-4" />
                {listProcessing ? "Processing..." : "Process Catalogue"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleReprocess()} disabled={listProcessing}>
                <RefreshCw className="mr-2 size-4" />
                Reprocess Search Names
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="mr-1.5 size-4" />
                Add Item
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Item</DialogTitle>
              </DialogHeader>
              <ItemForm
                categories={topCategories}
                subcategoriesFor={subcategoriesFor}
                storageTypes={storageTypesQuery.data ?? []}
                onSubmit={(data, allergens, aliases) =>
                  createItemMutation.mutate({ data, allergens, aliases })
                }
                isPending={createItemMutation.isPending}
                submitLabel="Add Item"
                listId={list.id}
                onEditExisting={(item) => {
                  setCreateOpen(false);
                  setEditingItem(item);
                }}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Processing progress — inline above table */}
      {(processingDialogOpen || listProcessing) && (
        <div className="rounded-lg border border-gray-200 p-4">
          <PreprocessingProgress listId={list.id} listName={list.name} />
          {!listProcessing && processingDialogOpen && (
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setProcessingDialogOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["admin", "inventory-items", list.id] });
                }}
              >
                Dismiss
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Items section — flex column for sticky pagination */}
      <div className="flex min-h-0 flex-1 flex-col">
      {/* Search + view toggle */}
      <div className="flex items-center gap-3 pb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search items..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>
        <div className="flex items-center rounded-md border border-gray-200">
          <Button
            size="sm"
            variant={view === "table" ? "secondary" : "ghost"}
            className="h-8 rounded-r-none px-2.5"
            onClick={() => {
              setView("table");
              setPage(0);
            }}
          >
            <Table2 className="size-4" />
          </Button>
          <Button
            size="sm"
            variant={view === "cards" ? "secondary" : "ghost"}
            className="h-8 rounded-l-none px-2.5"
            onClick={() => {
              setView("cards");
              setPage(0);
            }}
          >
            <LayoutGrid className="size-4" />
          </Button>
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editingItem} onOpenChange={(v) => !v && setEditingItem(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
            {editingItem?.clean_name && editingItem.clean_name !== editingItem.description && (
              <p className="text-sm text-gray-500">{editingItem.clean_name}</p>
            )}
          </DialogHeader>
          {editingItem && (
            <ItemForm
              categories={topCategories}
              subcategoriesFor={subcategoriesFor}
              storageTypes={storageTypesQuery.data ?? []}
              defaultCleanName={editingItem.clean_name ?? ""}
              defaultSimpleName={editingItem.simple_name ?? ""}
              defaultValues={{
                description: editingItem.description,
                sku: editingItem.sku ?? "",
                category_id: editingItem.category_id ?? "",
                subcategory_id: editingItem.subcategory_id ?? "",
                storage_type_id: editingItem.storage_type_id ?? "",
                unit_pack_size: editingItem.unit_pack_size ?? "",
                price: editingItem.price ?? undefined,
                currency: editingItem.currency,
                price_per_kg: editingItem.price_per_kg ?? undefined,
                typical_shelf_life_days: editingItem.typical_shelf_life_days ?? undefined,
                typical_weight_kg: editingItem.typical_weight_kg ?? undefined,
                edible_yield_pct: editingItem.edible_yield_pct ?? undefined,
                supplier: editingItem.supplier ?? "",
                status: editingItem.status as ItemFormData["status"],
              }}
              defaultAllergens={editingItem.allergens}
              defaultAliases={editingItem.aliases}
              onSubmit={(data, allergens, aliases, cleanName, simpleName) =>
                updateItemMutation.mutate({ itemId: editingItem.id, data, allergens, aliases, cleanName, simpleName })
              }
              isPending={updateItemMutation.isPending}
              submitLabel="Save Changes"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Scrollable content area */}
      <div className="min-h-0 flex-1 overflow-auto">
      {/* States */}
      {itemsQuery.isLoading && (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}
      {itemsQuery.isError && (
        <div className="rounded border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">Failed to load items</p>
        </div>
      )}
      {!itemsQuery.isLoading && items.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
          <Package className="size-8" />
          <p className="text-sm">
            {search ? "No items match your search" : "No items in this list"}
          </p>
        </div>
      )}

      {/* Table view */}
      {items.length > 0 && view === "table" && (
        <div className="rounded-lg border border-black/[0.06] bg-white">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40%]">
                  <SortHeader
                    label="Item"
                    field="description"
                    current={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                </TableHead>
                <TableHead>
                  <SortHeader
                    label="Supplier"
                    field="supplier"
                    current={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                  />
                </TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">
                  <SortHeader
                    label="Recipes"
                    field="recipe_count"
                    current={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="text-right">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SortHeader
                          label="Est. yield"
                          field="edible_yield_pct"
                          current={sortBy}
                          dir={sortDir}
                          onToggle={toggleSort}
                          align="right"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          Industry standard yield used for costing. This is not your tracked yield.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="text-right">
                  <SortHeader
                    label="Unit Price"
                    field="price"
                    current={sortBy}
                    dir={sortDir}
                    onToggle={toggleSort}
                    align="right"
                  />
                </TableHead>
                <TableHead className="w-8" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="group cursor-pointer border-black/[0.06] transition-all duration-[120ms] ease-out hover:border-l-2 hover:border-l-blue-500 hover:bg-black/[0.02] [&:first-child]:border-t-0 [&:last-child]:border-b-0" onClick={() => setEditingItem(item)}>
                  <TableCell className="py-2.5">
                    <div className="flex items-start gap-2">
                      {item.status !== "active" && (
                        <span
                          className={`mt-1.5 size-2 shrink-0 rounded-full ${item.status === "pending_review" || item.status === "seasonal" ? "bg-orange-500" : "bg-red-500"} ${item.status === "pending_review" && listProcessing ? "animate-pulse" : ""}`}
                          title={
                            item.status === "pending_review" && listProcessing
                              ? "Processing..."
                              : item.status
                          }
                        />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-semibold leading-tight text-gray-900">
                          {itemDisplayName(item)}
                          {item.allergens.length > 0 &&
                            item.allergens.map((a) => (
                              <Badge
                                key={a}
                                className="ml-1 rounded-full bg-orange-50 px-1.5 py-0 text-[10px] font-medium text-orange-700 hover:bg-orange-50"
                              >
                                {a}
                              </Badge>
                            ))}
                        </p>
                        {itemDisplayName(item) !== item.description && (
                          <p className="mt-0.5 text-xs text-gray-400 italic">{item.description}</p>
                        )}
                        <p className="mt-0.5 text-xs text-gray-400">
                          {[item.unit_pack_size, item.storage_types?.display_name]
                            .filter(Boolean)
                            .join(" · ") || "—"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2.5 text-sm text-gray-500">
                    {item.supplier || "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-xs text-gray-400">
                    {item.subcategory?.name || item.category?.name || "—"}
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    <span className={`text-sm font-medium tabular-nums ${item.recipe_count > 0 ? "text-gray-900" : "text-gray-300"}`}>
                      {item.recipe_count ?? 0}
                    </span>
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    {item.edible_yield_pct != null ? (
                      <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-gray-500">
                        <span className="relative h-1.5 w-10 rounded-full bg-gray-100">
                          <span
                            className={`absolute inset-y-0 left-0 rounded-full ${item.edible_yield_pct >= 80 ? "bg-[#639922]" : item.edible_yield_pct >= 65 ? "bg-[#EF9F27]" : "bg-[#E24B4A]"}`}
                            style={{ width: `${item.edible_yield_pct}%` }}
                          />
                        </span>
                        {item.edible_yield_pct}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-300">—</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    <p className="text-sm font-semibold tabular-nums text-gray-900">
                      {item.price != null ? `€${item.price.toFixed(2)}` : "—"}
                    </p>
                    {item.price_per_kg != null && (
                      <p className="text-[11px] tabular-nums text-gray-400">
                        €{item.price_per_kg.toFixed(2)}/kg
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-gray-400 opacity-0 hover:text-gray-600 group-hover:opacity-100"
                        >
                          <MoreHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditingItem(item)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            if (confirm(`Delete "${item.description}"?`))
                              deleteItemMutation.mutate(item.id);
                          }}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Card view */}
      {items.length > 0 && view === "cards" && (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((item) => (
              <button
                key={item.id}
                className="group rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
                onClick={() => setEditingItem(item)}
              >
                <div className="flex items-start justify-between">
                  <p className="text-sm font-semibold leading-tight text-gray-900">
                    {item.description}
                  </p>
                  {item.status !== "active" && (
                    <span
                      className={`mt-0.5 size-2 shrink-0 rounded-full ${item.status === "pending_review" || item.status === "seasonal" ? "bg-orange-500" : "bg-red-500"}`}
                      title={item.status}
                    />
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-400">
                  {[item.unit_pack_size, item.storage_types?.display_name]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </p>
                {item.supplier && <p className="mt-0.5 text-xs text-gray-400">{item.supplier}</p>}
                {item.price != null && (
                  <p className="mt-2 text-sm font-semibold tabular-nums text-gray-900">
                    €{item.price.toFixed(2)}
                    {item.price_per_kg != null && (
                      <span className="ml-1 text-[11px] font-normal text-gray-400">
                        €{item.price_per_kg.toFixed(2)}/kg
                      </span>
                    )}
                  </p>
                )}
                {item.edible_yield_pct != null && (
                  <p className="mt-1 inline-flex items-center gap-1.5 text-xs tabular-nums text-gray-500">
                    <span className="relative h-1 w-8 rounded-full bg-gray-100">
                      <span
                        className={`absolute inset-y-0 left-0 rounded-full ${item.edible_yield_pct >= 80 ? "bg-[#639922]" : item.edible_yield_pct >= 65 ? "bg-[#EF9F27]" : "bg-[#E24B4A]"}`}
                        style={{ width: `${item.edible_yield_pct}%` }}
                      />
                    </span>
                    {item.edible_yield_pct}%
                  </p>
                )}
                {item.allergens.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.allergens.map((a) => (
                      <Badge
                        key={a}
                        className="rounded-full bg-orange-50 px-1.5 py-0 text-[10px] text-orange-700 hover:bg-orange-50"
                      >
                        {a}
                      </Badge>
                    ))}
                  </div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
      </div>

      {/* Pagination — pinned at bottom */}
      {items.length > 0 && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
        />
      )}
      </div>
    </div>
  );
}

// ── Search Select ──────────────────────────────────────────────────────

function SearchSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedLabel = options.find((o) => o.id === value)?.label;

  const filtered = useMemo(() => {
    if (!query) return options.slice(0, 8);
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 8);
  }, [options, query]);

  if (selectedLabel) {
    return (
      <div className="flex items-center gap-1.5">
        <Badge variant="secondary" className="gap-1 rounded-full px-2 py-0.5 text-xs">
          {selectedLabel}
          <button
            type="button"
            onClick={() => onChange("")}
            className="text-gray-400 hover:text-red-500"
          >
            <X className="size-3" />
          </button>
        </Badge>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex gap-1">
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length > 0) {
              e.preventDefault();
              onChange(filtered[0].id);
              setQuery("");
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          className="h-8 text-xs"
        />
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute inset-x-0 top-9 z-50 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white p-1 shadow-md">
          {filtered.map((o) => (
            <button
              key={o.id}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(o.id);
                setQuery("");
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sort Header ────────────────────────────────────────────────────────

function SortHeader({
  label,
  field,
  current,
  dir,
  onToggle,
  align,
}: {
  label: string;
  field: string;
  current: string;
  dir: "asc" | "desc";
  onToggle: (f: string) => void;
  align?: "right";
}) {
  const active = current === field;
  return (
    <span
      role="button"
      tabIndex={0}
      className={`inline-flex cursor-pointer select-none items-center gap-1 ${align === "right" ? "justify-end" : ""}`}
      onClick={() => onToggle(field)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggle(field);
      }}
    >
      {label}
      <ArrowUpDown
        className={`size-3 ${active ? "text-gray-900" : "text-gray-300"} ${active && dir === "desc" ? "rotate-180" : ""}`}
      />
    </span>
  );
}

// ── Tag Input ──────────────────────────────────────────────────────────

function TagInput({
  tags,
  onTagsChange,
  suggestions,
  placeholder,
  id,
}: {
  tags: string[];
  onTagsChange: (t: string[]) => void;
  suggestions?: readonly string[];
  placeholder?: string;
  id?: string;
}) {
  const [input, setInput] = useState("");
  const filtered = useMemo(() => {
    if (!suggestions || !input) return [];
    const q = input.toLowerCase();
    return suggestions.filter((s) => s.includes(q) && !tags.includes(s));
  }, [suggestions, input, tags]);

  function add(tag: string) {
    const t = tag.trim().toLowerCase();
    if (t && !tags.includes(t)) onTagsChange([...tags, t]);
    setInput("");
  }
  function remove(tag: string) {
    onTagsChange(tags.filter((t) => t !== tag));
  }
  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    }
    if (e.key === "Backspace" && !input && tags.length > 0) remove(tags[tags.length - 1]);
  }

  return (
    <div>
      <div className="flex min-h-9 flex-wrap items-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 focus-within:border-gray-300 focus-within:ring-1 focus-within:ring-blue-500">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="gap-0.5 rounded-full px-2 py-0 text-xs">
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              className="ml-0.5 text-gray-400 hover:text-red-500"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ))}
        <input
          id={id}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="min-w-[80px] flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400"
        />
      </div>
      {filtered.length > 0 && (
        <div className="mt-1 rounded-md border border-gray-200 bg-white p-1 shadow-sm">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              className="block w-full rounded px-2 py-1 text-left text-xs text-gray-700 hover:bg-gray-100"
              onClick={() => add(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Loading Dots ───────────────────────────────────────────────────────

function useLoadingDots(active: boolean): string {
  const [count, setCount] = useState(1);
  useEffect(() => {
    if (!active) {
      setCount(1);
      return;
    }
    const id = setInterval(() => setCount((c) => (c % 3) + 1), 400);
    return () => clearInterval(id);
  }, [active]);
  return ".".repeat(count);
}

// ── Pill Selector ──────────────────────────────────────────────────────

function PillSelector({
  options,
  value,
  onChange,
  id,
  multi,
  selected,
  onToggle,
}: {
  options: { id: string; label: string }[];
  value?: string;
  onChange?: (v: string) => void;
  id?: string;
  multi?: boolean;
  selected?: string[];
  onToggle?: (v: string) => void;
}) {
  return (
    <div id={id} className="flex flex-wrap gap-1.5" role={multi ? "group" : "radiogroup"}>
      {options.map((opt) => {
        const isActive = multi ? (selected ?? []).includes(opt.id) : value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role={multi ? "checkbox" : "radio"}
            aria-checked={isActive}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            onClick={() => (multi ? onToggle?.(opt.id) : onChange?.(opt.id))}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Collapsible Section ────────────────────────────────────────────────

function Section({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 py-1 text-xs font-medium uppercase tracking-wider text-gray-400 hover:text-gray-600"
        onClick={onToggle}
      >
        <ChevronDown className={`size-3 transition-transform ${open ? "" : "-rotate-90"}`} />
        {title}
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${open ? "mt-3 max-h-[2000px] opacity-100" : "max-h-0 opacity-0"}`}
      >
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

// ── Item Form (Linear-inspired) ────────────────────────────────────────

function ItemForm({
  defaultValues,
  defaultAllergens,
  defaultAliases,
  defaultCleanName,
  defaultSimpleName,
  categories,
  subcategoriesFor,
  storageTypes,
  onSubmit,
  isPending,
  submitLabel,
  listId,
  onEditExisting,
}: {
  defaultValues?: Partial<ItemFormData>;
  defaultAllergens?: string[];
  defaultAliases?: string[];
  defaultCleanName?: string;
  defaultSimpleName?: string;
  categories: FoodCategory[];
  subcategoriesFor: (parentId: string) => FoodCategory[];
  storageTypes: StorageType[];
  onSubmit: (data: ItemFormData, allergens: string[], aliases: string[], cleanName?: string, simpleName?: string) => void;
  isPending: boolean;
  submitLabel: string;
  listId?: string;
  onEditExisting?: (item: InventoryItem) => void;
}) {
  const headers = useApiHeaders();
  const [allergens, setAllergens] = useState<string[]>(defaultAllergens ?? []);
  const [aliases, setAliases] = useState<string[]>(defaultAliases ?? []);
  const [cleanName, setCleanName] = useState(defaultCleanName ?? "");
  const [simpleName, setSimpleName] = useState(defaultSimpleName ?? "");
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<InventoryItem | null>(null);
  const [productOpen, setProductOpen] = useState(!!defaultValues);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [specsOpen, setSpecsOpen] = useState(false);
  const dots = useLoadingDots(generating);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<ItemFormData>({
    resolver: zodResolver(itemFormSchema),
    defaultValues: { currency: "EUR", status: "active", edible_yield_pct: 75, ...defaultValues },
  });

  const selectedCategoryId = watch("category_id");
  const subcategories = selectedCategoryId ? subcategoriesFor(selectedCategoryId) : [];
  const price = watch("price");
  const unitStr = watch("unit_pack_size");

  const autoPricePerKg = useMemo(() => {
    if (!price || !unitStr) return null;
    const weight = parseWeightFromUnit(unitStr);
    if (!weight || weight <= 0) return null;
    return Math.round((price / weight) * 100) / 100;
  }, [price, unitStr]);

  async function runGenerate(desc: string) {
    setSuggestion(null);
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/generate`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          description: desc,
          unit_pack_size: getValues("unit_pack_size") || null,
          price: getValues("price") || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail ?? err?.error?.message ?? `${res.status}: ${res.statusText}`);
      }
      const { data } = await res.json();

      // Pre-fill next SKU from list code + item count
      if (listId) {
        try {
          const listRes = await fetch(`${API_URL}/api/v1/admin/inventory-lists`, { headers });
          if (listRes.ok) {
            const lists = (await listRes.json()).data as InventoryList[];
            const currentList = lists.find((l: InventoryList) => l.id === listId);
            if (currentList) {
              const nextNum = currentList.item_count + 1;
              setValue("sku", `${currentList.list_code}${String(nextNum).padStart(5, "0")}`);
            }
          }
        } catch {
          /* non-critical */
        }
      }

      // Populate form fields — set category first, subcategory after
      if (data.unit_pack_size) setValue("unit_pack_size", data.unit_pack_size);
      if (data.price) setValue("price", data.price);
      if (data.storage_type_id) setValue("storage_type_id", data.storage_type_id);
      if (data.price_per_kg) setValue("price_per_kg", data.price_per_kg);
      if (data.typical_shelf_life_days)
        setValue("typical_shelf_life_days", data.typical_shelf_life_days);
      if (data.typical_weight_kg) setValue("typical_weight_kg", data.typical_weight_kg);
      if (data.edible_yield_pct != null) setValue("edible_yield_pct", data.edible_yield_pct);
      if (data.allergens?.length) setAllergens(data.allergens);
      if (data.aliases?.length) setAliases(data.aliases);

      // Set category, then subcategory on next tick so the dropdown renders
      if (data.category_id) {
        setValue("category_id", data.category_id);
        if (data.subcategory_id) {
          requestAnimationFrame(() => setValue("subcategory_id", data.subcategory_id));
        }
      }

      // Open all sections to show generated values
      setProductOpen(true);
      setPricingOpen(true);
      setSpecsOpen(true);
      toast.success("Fields populated by AI");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to generate");
    } finally {
      setGenerating(false);
    }
  }

  async function handleGenerate() {
    const desc = getValues("description");
    if (!desc?.trim()) {
      toast.error("Enter a description first");
      return;
    }
    setSuggestion(null);
    setDuplicate(null);

    // Step 1: Check for duplicates in the current list
    if (listId) {
      try {
        const params = new URLSearchParams({ search: desc, limit: "1" });
        const res = await fetch(
          `${API_URL}/api/v1/admin/inventory-lists/${listId}/items?${params}`,
          { headers },
        );
        if (res.ok) {
          const { data } = await res.json();
          if (data?.length > 0) {
            const match = data[0] as InventoryItem;
            // Check if name is a close match (case-insensitive)
            if (
              match.description.toLowerCase().includes(desc.toLowerCase()) ||
              desc.toLowerCase().includes(match.description.toLowerCase())
            ) {
              setDuplicate(match);
              return;
            }
          }
        }
      } catch {
        /* continue */
      }
    }

    // Step 2: Check if it's a food item
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/validate-description`, {
        method: "POST",
        headers,
        body: JSON.stringify({ description: desc }),
      });
      if (res.ok) {
        const { data } = await res.json();
        if (!data.is_food) {
          setSuggestion(data.suggestion ?? null);
          return;
        }
      }
    } catch {
      /* continue */
    }

    // Step 3: Generate
    await runGenerate(desc);
  }

  async function handleSuggestionClick() {
    if (!suggestion) return;
    setValue("description", suggestion);
    setSuggestion(null);
    await runGenerate(suggestion);
  }

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (autoPricePerKg && !data.price_per_kg) data.price_per_kg = autoPricePerKg;
        onSubmit(data, allergens, aliases, cleanName || undefined, simpleName || undefined);
      })}
      className="space-y-6"
    >
      {/* Description + Generate — always visible */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="description">
          Description <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-2">
          <Input
            id="description"
            className="flex-1 text-base"
            placeholder="e.g. Chicken Breast Fillets"
            {...register("description")}
          />
          <Button
            type="button"
            variant="outline"
            className="shrink-0 gap-1.5 px-3"
            disabled={generating}
            onClick={handleGenerate}
          >
            <Sparkles className={`size-4 ${generating ? "animate-pulse text-amber-500" : ""}`} />
            {generating ? "Generating..." : "Generate"}
          </Button>
        </div>
        {errors.description && (
          <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>
        )}
        {duplicate && (
          <p className="mt-1.5 text-xs text-gray-500">
            <span className="font-medium text-gray-700">{duplicate.description}</span> is already in
            this inventory.{" "}
            {onEditExisting && (
              <button
                type="button"
                className="font-medium text-blue-600 hover:underline"
                onClick={() => {
                  onEditExisting(duplicate);
                  setDuplicate(null);
                }}
              >
                Edit it instead?
              </button>
            )}
          </p>
        )}
        {suggestion && !duplicate && (
          <p className="mt-1.5 text-xs text-gray-500">
            Did you mean{" "}
            <button
              type="button"
              className="font-medium text-blue-600 hover:underline"
              onClick={handleSuggestionClick}
            >
              {suggestion}
            </button>
            ?
          </p>
        )}
      </div>

      {/* Clean Name (search name) */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="admin-clean-name">
          Search Name <span className="text-xs text-gray-300">(used for ingredient matching)</span>
        </label>
        <Input
          id="admin-clean-name"
          className="text-sm"
          placeholder="AI-generated clean name for search"
          value={cleanName}
          onChange={(e) => setCleanName(e.target.value)}
        />
      </div>

      {/* Simple Name (core ingredient) */}
      <div>
        <label className="mb-1 block text-xs text-gray-400" htmlFor="admin-simple-name">
          Simple Name <span className="text-xs text-gray-300">(core ingredient, no brand/pack size)</span>
        </label>
        <Input
          id="admin-simple-name"
          className="text-sm"
          placeholder="AI-generated simple ingredient name"
          value={simpleName}
          onChange={(e) => setSimpleName(e.target.value)}
        />
      </div>

      {/* Product Info */}
      <Section
        title="Product Info"
        open={productOpen}
        onToggle={() => setProductOpen(!productOpen)}
      >
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="sku">
              SKU
            </label>
            <Input id="sku" placeholder="Optional" {...register("sku")} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="unit_pack_size">
              Unit / Pack Size
            </label>
            <Input
              id="unit_pack_size"
              placeholder={generating ? dots : "e.g. 5kg box"}
              {...register("unit_pack_size")}
            />
          </div>
        </div>
        {storageTypes.length > 0 && (
          <div>
            <label className="mb-1.5 block text-xs text-gray-400" htmlFor="pill-storage-type">
              Storage Type
            </label>
            <PillSelector
              id="pill-storage-type"
              options={storageTypes.map((st) => ({ id: st.id, label: st.display_name }))}
              value={watch("storage_type_id") ?? ""}
              onChange={(v) => setValue("storage_type_id", v)}
            />
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-xs text-gray-400" htmlFor="pill-allergens">
            Allergens (EU 14)
          </label>
          <PillSelector
            id="pill-allergens"
            options={EU_14_ALLERGENS.map((a) => ({ id: a, label: a }))}
            multi
            selected={allergens}
            onToggle={(v) => {
              setAllergens((prev) =>
                prev.includes(v) ? prev.filter((a) => a !== v) : [...prev, v],
              );
            }}
          />
        </div>
        <div className="w-full">
          <label className="mb-1 block text-xs text-gray-400" htmlFor="form-category">
            Category
          </label>
          <Select
            value={selectedCategoryId ?? ""}
            onValueChange={(v) => {
              setValue("category_id", v);
              setValue("subcategory_id", "");
            }}
          >
            <SelectTrigger id="form-category" className="w-full">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-full">
          <label className="mb-1 block text-xs text-gray-400" htmlFor="form-subcategory">
            Subcategory
          </label>
          <Select
            value={watch("subcategory_id") ?? ""}
            onValueChange={(v) => setValue("subcategory_id", v)}
            disabled={!selectedCategoryId}
          >
            <SelectTrigger id="form-subcategory" className="w-full">
              <SelectValue
                placeholder={selectedCategoryId ? "Select subcategory" : "Select category first"}
              />
            </SelectTrigger>
            <SelectContent>
              {subcategories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="aliases-input">
            Aliases
          </label>
          <TagInput
            id="aliases-input"
            tags={aliases}
            onTagsChange={setAliases}
            placeholder="Type and press Enter"
          />
          <p className="mt-0.5 text-xs text-gray-400">Alternative names for AI matching</p>
        </div>
      </Section>

      {/* Pricing */}
      <Section title="Pricing" open={pricingOpen} onToggle={() => setPricingOpen(!pricingOpen)}>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="price">
              Price (€)
            </label>
            <Input
              id="price"
              type="number"
              step="0.01"
              placeholder={generating ? dots : undefined}
              {...register("price")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="price_per_kg">
              Price per kg (€)
            </label>
            <Input
              id="price_per_kg"
              type="number"
              step="0.01"
              placeholder={autoPricePerKg ? `Auto: €${autoPricePerKg.toFixed(2)}` : "—"}
              {...register("price_per_kg")}
            />
            {autoPricePerKg && (
              <p className="mt-0.5 text-xs text-gray-400">
                Calculated: €{autoPricePerKg.toFixed(2)}/kg
              </p>
            )}
          </div>
        </div>
      </Section>

      {/* Specifications */}
      <Section title="Specifications" open={specsOpen} onToggle={() => setSpecsOpen(!specsOpen)}>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="typical_shelf_life_days">
              Shelf Life (days)
            </label>
            <Input
              id="typical_shelf_life_days"
              type="number"
              placeholder={generating ? dots : undefined}
              {...register("typical_shelf_life_days")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="typical_weight_kg">
              Weight (kg)
            </label>
            <Input
              id="typical_weight_kg"
              type="number"
              step="0.01"
              placeholder={generating ? dots : undefined}
              {...register("typical_weight_kg")}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-400" htmlFor="edible_yield_pct">
              Yield (%)
            </label>
            <Input
              id="edible_yield_pct"
              type="number"
              step="0.1"
              placeholder={generating ? dots : undefined}
              {...register("edible_yield_pct")}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-400" htmlFor="supplier">
            Supplier
          </label>
          <Input id="supplier" {...register("supplier")} />
        </div>
      </Section>

      <Button type="submit" className="w-full" disabled={isPending}>
        {isPending ? "Saving..." : submitLabel}
      </Button>
    </form>
  );
}
